import React, { useCallback, useEffect, useState } from "react";
import { Gift, Coins, Trophy, Clock, Users, Ticket, Plus, Trash2, Sparkles, Lock, Crown, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fmtLocal } from "../utils/time";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString();
const imgSrc = (u) => (u && u.startsWith("/api/") ? `${BASE}${u}` : u);

const STATUS = {
  live: { label: "Live", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  upcoming: { label: "Upcoming", cls: "text-sky-300 border-sky-400/40 bg-sky-400/10" },
  closed: { label: "Drawing soon", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  ended: { label: "Winner announced", cls: "text-[#e6c25a] border-[#e6c25a]/40 bg-[#e6c25a]/10" },
};

function useCountdown(iso) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    if (!iso) return;
    const tick = () => {
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return setLeft("Ended");
      const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5), m = Math.floor((ms % 36e5) / 6e4), s = Math.floor((ms % 6e4) / 1e3);
      setLeft(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
}

const api = async (path, opts = {}) => {
  const res = await fetch(`${API}/giveaways${path}`, { credentials: "include", ...opts });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Something went wrong");
  return j;
};

const GiveawayCard = ({ g, meta, onChange }) => {
  const left = useCountdown(g.status === "live" ? g.ends_at : null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState("");
  const st = STATUS[g.status] || STATUS.live;
  const cost = qty * (g.coins_per_entry || 0);
  const odds = g.total_entries ? Math.round((g.my_entries / g.total_entries) * 100) : 0;

  const enter = async () => {
    setBusy("enter");
    try {
      const r = await api(`/${g.id}/enter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entries: qty }) });
      toast.success(g.mode === "free" ? "You're in! Good luck." : `${qty} entr${qty > 1 ? "ies" : "y"} added · -${fmt(r.spent)} WS Coins`);
      onChange();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };
  const draw = async () => {
    if (!window.confirm("Draw the winner now? This cannot be undone.")) return;
    setBusy("draw");
    try { await api(`/${g.id}/draw`, { method: "POST" }); toast.success("Winner drawn!"); onChange(); } catch (e) { toast.error(e.message); }
    setBusy("");
  };
  const del = async () => {
    if (!window.confirm("Delete this giveaway? Coin entries will be refunded.")) return;
    setBusy("del");
    try { await api(`/${g.id}`, { method: "DELETE" }); toast.success("Giveaway removed"); onChange(); } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  return (
    <div className="ws-panel ws-panel-hover overflow-hidden ws-fade-up flex flex-col" data-testid={`giveaway-card-${g.id}`}>
      <div className="relative h-[170px] bg-[radial-gradient(circle_at_30%_20%,rgba(230,194,90,0.25),transparent_60%),linear-gradient(135deg,#0b1120,#141a33)]">
        {g.image ? <img src={imgSrc(g.image)} alt={g.title} className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center"><Gift className="w-14 h-14 text-[#e6c25a]/70" /></div>}
        <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full border ${st.cls}`} data-testid="giveaway-status">{st.label}</span>
        {g.status === "live" && left && (
          <span className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-black/55 border border-white/10 text-white flex items-center gap-1" data-testid="giveaway-countdown"><Clock className="w-3 h-3" />{left}</span>
        )}
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#e6c25a] font-semibold flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" />{g.prize}</div>
          <h3 className="ws-heading text-[19px] font-bold text-white mt-1 leading-tight">{g.title}</h3>
          {g.desc && <p className="text-[13px] text-[color:var(--ws-muted)] mt-1.5 leading-relaxed">{g.desc}</p>}
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          {[[Users, fmt(g.participants), "Traders"], [Ticket, fmt(g.total_entries), "Entries"],
            [g.mode === "free" ? Sparkles : Coins, g.mode === "free" ? "Free" : fmt(g.coins_per_entry), g.mode === "free" ? "1 per trader" : "Coins / entry"]].map(([I, v, l]) => (
            <div key={l} className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[rgba(255,255,255,0.02)] py-2">
              <I className="w-3.5 h-3.5 mx-auto text-[color:var(--ws-muted)]" />
              <div className="text-[15px] font-semibold text-white mt-0.5">{v}</div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--ws-muted)]">{l}</div>
            </div>
          ))}
        </div>

        {g.winner ? (
          <div className="rounded-xl border border-[#e6c25a]/40 bg-[#e6c25a]/10 p-3 flex items-center gap-3" data-testid="giveaway-winner">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-[#e6c25a]/20 flex items-center justify-center shrink-0">
              {g.winner.picture ? <img src={g.winner.picture} alt="" className="w-full h-full object-cover" /> : <Crown className="w-5 h-5 text-[#e6c25a]" />}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#e6c25a]">Winner</div>
              <div className="text-white font-semibold truncate">{g.winner.name}</div>
              <div className="text-[11px] text-[color:var(--ws-muted)]">{g.winner.entries} entr{g.winner.entries > 1 ? "ies" : "y"} · {fmtLocal(g.winner.drawn_at, { withYear: false })}</div>
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-[color:var(--ws-muted)] flex items-center justify-between">
            <span>Ends {fmtLocal(g.ends_at, { withYear: false })}</span>
            {g.my_entries > 0 && <span className="text-emerald-300 font-medium" data-testid="giveaway-my-entries">You: {g.my_entries} · {odds}% odds</span>}
          </div>
        )}

        <div className="mt-auto pt-1">
          {g.status === "live" && !g.winner && (
            !meta.broker_connected ? (
              <Link to="/connect" className="ws-btn-ghost w-full justify-center flex items-center gap-2 text-[13px] py-2.5 rounded-xl" data-testid="giveaway-connect-broker">
                <Lock className="w-4 h-4" /> Connect broker to enter
              </Link>
            ) : g.mode === "free" ? (
              <button onClick={enter} disabled={busy || g.my_entries > 0} className="ws-btn-primary w-full py-2.5 rounded-xl text-[13px] font-semibold disabled:opacity-60" data-testid="giveaway-enter-btn">
                {g.my_entries > 0 ? "You're entered" : busy === "enter" ? "Entering…" : "Enter for free"}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-xl border border-[rgba(148,163,184,0.2)] overflow-hidden">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-white hover:bg-white/5" data-testid="giveaway-qty-minus">−</button>
                  <span className="w-8 text-center text-white text-[14px] font-semibold" data-testid="giveaway-qty">{qty}</span>
                  <button onClick={() => setQty(Math.min(100, qty + 1))} className="px-3 py-2 text-white hover:bg-white/5" data-testid="giveaway-qty-plus">+</button>
                </div>
                <button onClick={enter} disabled={busy || meta.balance < cost} className="ws-btn-primary flex-1 py-2.5 rounded-xl text-[13px] font-semibold disabled:opacity-60 flex items-center justify-center gap-1.5" data-testid="giveaway-enter-btn">
                  <Coins className="w-4 h-4" /> {busy === "enter" ? "Entering…" : meta.balance < cost ? `Need ${fmt(cost)} coins` : `Enter · ${fmt(cost)} coins`}
                </button>
              </div>
            )
          )}
          {meta.is_admin && (
            <div className="flex gap-2 mt-2">
              {!g.winner && <button onClick={draw} disabled={!!busy} className="flex-1 text-[12px] py-2 rounded-xl border border-[#e6c25a]/40 text-[#e6c25a] hover:bg-[#e6c25a]/10 transition-colors flex items-center justify-center gap-1.5" data-testid="giveaway-draw-btn"><Trophy className="w-3.5 h-3.5" /> Draw winner</button>}
              <button onClick={del} disabled={!!busy} className="text-[12px] py-2 px-3 rounded-xl border border-red-400/30 text-red-300 hover:bg-red-400/10 transition-colors" data-testid="giveaway-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateGiveaway = ({ onCreated, onClose }) => {
  const [f, setF] = useState({ title: "", prize: "", desc: "", mode: "coins", coins_per_entry: 50, ends_at: "", image: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await fetch(`${API}/giveaways/upload`, { method: "POST", credentials: "include", body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Upload failed");
      setF((p) => ({ ...p, image: j.url })); toast.success("Image uploaded");
    } catch (er) { toast.error(er.message); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api("", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : "" }) });
      toast.success("Giveaway is live!"); onCreated(); onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const inp = "w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(148,163,184,0.2)] rounded-xl px-3 py-2.5 text-[14px] text-white outline-none focus:border-[#e6c25a]/60";

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="giveaway-create-modal">
      <div className="ws-panel w-full max-w-[560px] p-6 relative max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[color:var(--ws-muted)] hover:text-white" data-testid="giveaway-create-close"><X className="w-5 h-5" /></button>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#e6c25a] mb-1">Admin</div>
        <h2 className="ws-heading text-[24px] font-bold text-white mb-5">New Giveaway</h2>
        <div className="space-y-3">
          <input className={inp} placeholder="Title (e.g. Weekly $100 Cash Drop)" value={f.title} onChange={set("title")} data-testid="gw-title" />
          <input className={inp} placeholder="Prize (e.g. $100 cash · iPhone · 30d VIP)" value={f.prize} onChange={set("prize")} data-testid="gw-prize" />
          <textarea className={`${inp} min-h-[80px]`} placeholder="Description / rules (optional)" value={f.desc} onChange={set("desc")} data-testid="gw-desc" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[color:var(--ws-muted)]">Entry mode</label>
              <select className={inp} value={f.mode} onChange={set("mode")} data-testid="gw-mode">
                <option value="coins">WS Coins per entry (multiple)</option>
                <option value="free">Free · 1 entry per trader</option>
              </select>
            </div>
            {f.mode === "coins" && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[color:var(--ws-muted)]">Coins per entry</label>
                <input type="number" min="1" className={inp} value={f.coins_per_entry} onChange={set("coins_per_entry")} data-testid="gw-coins" />
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[color:var(--ws-muted)]">Ends at</label>
            <input type="datetime-local" className={inp} value={f.ends_at} onChange={set("ends_at")} data-testid="gw-ends" />
          </div>
          <div className="flex items-center gap-3">
            <label className="ws-btn-ghost text-[13px] px-3 py-2 rounded-xl cursor-pointer flex items-center gap-2">
              <ImagePlus className="w-4 h-4" /> {f.image ? "Change image" : "Prize image"}
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gw-image" />
            </label>
            {f.image && <img src={imgSrc(f.image)} alt="" className="h-12 w-20 object-cover rounded-lg border border-white/10" />}
          </div>
          <button onClick={submit} disabled={busy} className="ws-btn-primary w-full py-3 rounded-xl font-semibold disabled:opacity-60" data-testid="gw-submit">{busy ? "Creating…" : "Launch giveaway"}</button>
        </div>
      </div>
    </div>
  );
};

export default function GiveawayZone() {
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api("")); } catch { setData({ giveaways: [], is_admin: false, broker_connected: false, balance: 0 }); }
  }, []);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  if (!data) return <div className="ws-panel p-10 m-8 text-center text-[color:var(--ws-muted)]" data-testid="giveaway-loading">Loading Giveaway Zone…</div>;
  const live = data.giveaways.filter((g) => g.status !== "ended");
  const past = data.giveaways.filter((g) => g.status === "ended");

  return (
    <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-8" data-testid="giveaway-zone">
      <div className="flex items-center gap-3 mb-2 ws-fade-up">
        <div className="ws-icon-chip w-9 h-9" style={{ color: "#e6c25a" }}><Gift className="w-5 h-5" /></div>
        <span className="text-[11px] tracking-[0.28em] text-[color:var(--ws-muted)] uppercase">Win while you trade</span>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="ws-heading text-[34px] sm:text-[40px] font-bold ws-title-gradient ws-fade-up" style={{ animationDelay: "40ms" }}>WS Giveaway Zone</h1>
          <p className="text-[14px] text-[color:var(--ws-muted)] mt-1 ws-fade-up" style={{ animationDelay: "80ms" }}>Cash, gadgets & VIP perks — enter with WS Coins or for free. Winners are drawn transparently and announced right here.</p>
        </div>
        <div className="flex items-center gap-3 ws-fade-up" style={{ animationDelay: "100ms" }}>
          <div className="ws-panel px-4 py-2.5 flex items-center gap-2" data-testid="giveaway-balance">
            <Coins className="w-4 h-4 text-[#e6c25a]" /><span className="text-white font-semibold">{fmt(data.balance)}</span><span className="text-[12px] text-[color:var(--ws-muted)]">WS Coins</span>
            <Link to="/growth-zone" className="text-[12px] text-[color:var(--ws-cyan)] hover:underline ml-1">Earn more</Link>
          </div>
          {data.is_admin && (
            <button onClick={() => setShowCreate(true)} className="ws-btn-primary px-4 py-2.5 rounded-xl text-[13px] font-semibold flex items-center gap-2" data-testid="giveaway-create-btn"><Plus className="w-4 h-4" /> New giveaway</button>
          )}
        </div>
      </div>

      {!data.broker_connected && (
        <div className="ws-panel p-4 mb-6 flex flex-wrap items-center justify-between gap-3 border-[#e6c25a]/30 ws-fade-up" data-testid="giveaway-gate-banner">
          <div className="flex items-center gap-3 text-[13px] text-white"><Lock className="w-4 h-4 text-[#e6c25a]" /> Giveaways are exclusive to traders with a connected Elefin or XM account.</div>
          <Link to="/connect" className="ws-btn-primary px-4 py-2 rounded-xl text-[12px] font-semibold">Connect broker</Link>
        </div>
      )}

      {live.length === 0 && (
        <div className="ws-panel p-12 text-center ws-fade-up" data-testid="giveaway-empty">
          <Gift className="w-12 h-12 mx-auto text-[#e6c25a]/60 mb-3" />
          <div className="ws-heading text-[20px] font-bold text-white">No live giveaways right now</div>
          <p className="text-[13px] text-[color:var(--ws-muted)] mt-1">Keep journaling and stacking WS Coins — the next drop is coming.</p>
        </div>
      )}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {live.map((g) => <GiveawayCard key={g.id} g={g} meta={data} onChange={load} />)}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="ws-heading text-lg font-bold text-white mt-12 mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-[#e6c25a]" /> Past winners</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {past.map((g) => <GiveawayCard key={g.id} g={g} meta={data} onChange={load} />)}
          </div>
        </>
      )}

      {showCreate && <CreateGiveaway onCreated={load} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
